import {MutableRefObject, useContext} from 'react';
import {isShobiddak} from '../../../helpers/commonFunctions';
import {useLang} from '../../../i18n/OpenSooqi18n';
import Cookies from 'js-cookie';
import {Message, Room} from './types';
import {AuthContext} from '../../../i18n/AuthProvider';
import DynamicObject from '../../../../models/dynamic-object';
import {saveAs} from 'file-saver';

export function classNames(...args: (string | false | null | undefined)[]) {
  const output: string[] = [];
  for (let i = 0; i < args.length; i++) {
    if (args[i]) output.push(args[i] as string);
  }
  return {className: output.join(' ')};
}

export const generateRandomNumber = () => {
  return Math.floor(10000000000 + Math.random() * 90000000000);
};

export const redirectToOpenSooq = (pid: number) => {
  const baseLink = process.env.REACT_APP_OPENSOOQ_BASE;
  const lang = Cookies.get('NEXT_LOCALE') === 'ku' ? 'ar' : Cookies.get('NEXT_LOCALE');
  const country = Cookies.get('ecountry');
  if (!baseLink || !lang || !country) return;
  const postLink = generateOpenSooqRedirectUrl(baseLink, country, lang, pid);
  window.open(postLink, 'blank');
};

const generateOpenSooqRedirectUrl = (url: string, country: string, lang: string, pid: number) => {
  let redirectTo = url?.replace('{country}', country ?? '').replace('{lang}', lang ?? '');
  if (isShobiddak()) {
    redirectTo = url?.replace('{country}.', '').replace('/{lang}', '');
  }
  return `${redirectTo}/search/${pid}`;
};

export const getDirection = (text: string): 'ltr' | 'rtl' => {
  const rtlPattern = /[\u0600-\u06FF]/;
  return rtlPattern.test(text) ? 'rtl' : 'ltr';
};

export const redirectToEditPost = (identifier: string, pid: number, pointOfEntry?: string) => {
  const addPostBaseLink = process.env.REACT_APP_ADD_POST;
  const lang = Cookies.get('NEXT_LOCALE') === 'ku' ? 'ar' : Cookies.get('NEXT_LOCALE');
  const source = Cookies.get('source');
  if (!addPostBaseLink) return;
  const resolvedPointOfEntry = pointOfEntry || Cookies.get("sessionFunnelPointOfEntry") || "";
  const pointOfEntryParam = resolvedPointOfEntry ? `&point_of_entry=${encodeURIComponent(resolvedPointOfEntry)}` : "";
  const editPostLink = `${addPostBaseLink}?v=2&source=${source}&lang=${lang}&flow=edit&draft_id=0&post_id=${pid}&return=${window.location.href}${pointOfEntryParam}&scrollByIdentifier=${identifier}`;
  window.open(editPostLink, 'blank');
};

export const phonePattern = /(?:^|\s)\+?[0-9\u0660-\u0669\u06F0-\u06F9-]{9,14}(?=$|\s)/g;

export const maskPhoneNumber = (number: string): string => {
  const maskedPart = number.slice(2).replace(/./g, 'X');
  return `\u200E${number.slice(0, 2)}${maskedPart}\u200E`;
};

// export function setHeightDynamically(ref: React.RefObject<HTMLDivElement>) {
//   const screenHeight = window.innerHeight

//   // Access the #pageTitle directly from the document
//   const titleElement = document.getElementById('page-heading')
//   const headerElement = document.getElementById('kt_app_toolbar')
//   const navElement = document.getElementById('navBar')
//   const modalHeader = document.getElementById('modal-header')
//   const chatHeader = document.getElementById('chatHeader')
//   const modalHeaderHeight = modalHeader ? modalHeader.offsetHeight : 0
//   const titleHeight = titleElement ? titleElement.offsetHeight : 0
//   const headerHeight = headerElement ? headerElement.offsetHeight : 0
//   const chatHeaderHeight = chatHeader ? chatHeader.offsetHeight : 0
//   const navHeight = navElement ? navElement.offsetHeight : 0
//   const chatHeight = screenHeight - (titleHeight + headerHeight + navHeight + modalHeaderHeight + chatHeaderHeight) - 32

//   if (ref.current) {
//     ref.current.style.height = `${chatHeight}px`
//   }
// }
export function setHeightDynamically(ref: React.RefObject<HTMLDivElement>) {
  if (!ref.current) return;

  const screenHeight = window.innerHeight;
  const refTop = ref.current.getBoundingClientRect().top;
  const chatHeight = screenHeight - refTop;

  ref.current.style.height = `${chatHeight}px`;
}



export function setHeightDynamicallyForChatMsgs(
  ref: React.RefObject<HTMLDivElement>,
  viewportHeight?: number
) {
  if (!ref.current) return;

  const effectiveViewportHeight =
    viewportHeight ?? window.visualViewport?.height ?? window.innerHeight;
  const viewportOffsetTop = window.visualViewport?.offsetTop ?? 0;
  const refTop = ref.current.getBoundingClientRect().top + viewportOffsetTop;
  const formHeight = document.getElementById('chatForm')?.offsetHeight ?? 0;
  const safeAreaBottom = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom') || '0'
  );

  const calculatedHeight = effectiveViewportHeight - refTop - formHeight - safeAreaBottom;
  const chatHeight = Math.max(calculatedHeight, 0);

  ref.current.style.height = `${chatHeight}px`;
}


export const extractUrls = (text: string): string[] => {
  const urlPattern =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;
  const urls = text.match(urlPattern) || [];
  return urls;
};

export const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

export const maskEmail = (email: string, userIsSender: boolean): string => {
  if (userIsSender) {
    return email;
  }
  const [localPart, domain] = email.split('@');
  if (localPart) {
    if (localPart.length <= 2) {
      return `${localPart[0]}***@${domain}`;
    }
    return `${localPart[0]}***${localPart.slice(-1)}@${domain}`;
  }
  return '';
};

export const convertToHuman = (value: number | string | Date): DynamicObject => {
  const currentDate = new Date();
  const date = new Date(value);
  let interval = (currentDate.getTime() - date.getTime()) / 1000;

  if (interval < 60) {
    return {
      key: 'just_now',
    };
  }

  interval = interval / 60;

  if (interval < 60) {
    // less than 1 hour
    return {
      key: 'minutes_ago',
      interval: Math.floor(interval),
    };
  }

  interval = interval / 60;

  if (interval < 24) {
    // less than 1 day
    return {
      key: 'hours_ago',
      interval: Math.floor(interval),
    };
  }

  interval = interval / 24;

  if (interval < 30) {
    // less than 1 month
    return {
      key: 'days_ago',
      interval: Math.floor(interval),
    };
  }

  interval = interval / 30;

  if (interval < 12) {
    // less than 1 year
    return {
      key: 'months_ago',
      interval: Math.floor(interval),
    };
  }

  interval = interval / 12;

  return {
    key: 'years_ago',
    interval: Math.floor(interval),
  };
};

export const formattingResponseTime = (second: number) => {
  let interval = Math.floor(second / 60);

  if (interval <= 1) {
    return {
      key: 'less_than_minute',
    };
  }

  if (interval < 60) {
    return {
      key: 'minutes_duration',
      interval,
    };
  }

  interval = Math.floor(interval / 60);

  if (interval < 24) {
    return {
      key: 'hours_duration',
      interval,
    };
  }
  return {
    key: 'response_in_days',
  };
};
export const getTypeConfig = (mime: string) => {
  switch (true) {
    case /^image(s)?.*$/.test(mime):
      return {
        type: 32,
        subType: 320,
      };
    case /^video.*$/.test(mime):
      return {
        type: 34,
        subType: 340,
      };
    case /^audio.*$/.test(mime):
      return {
        type: 36,
        subType: 360,
      };
    default:
      return {
        type: 40,
        subType: 400,
      };
  }
};
export const isIphone = (): boolean => {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const maxTouch = navigator.maxTouchPoints || 0;

  const isiPhone = /iPhone/.test(ua) && !('MSStream' in window);
  const isModernIpad = platform === 'MacIntel' && maxTouch > 1;
  const isRealMobile = /Mobile|iPhone/.test(ua) && maxTouch > 0;

  return isiPhone && !isModernIpad && isRealMobile;
};

export const handleDownload = (imagePath?: string) => {
  if (!imagePath) return;

  const filename = imagePath.split('/')?.pop();

  saveAs(imagePath, filename);
};
